// Keyboard mode for the claims screens (#11422).
//
// The letter shortcuts are opt-in: off, the queue and the review screen
// answer only `?`, Esc, Enter or Space on a focused control, and the grid's
// own keys; no key hint shows. A reviewer turns it on in the `?` overlay, and
// the choice is kept per viewer in this browser and applies to both screens.
//
// The stored value is the state; nothing is held at module scope. A change in
// this tab is announced with a window event, one in another tab arrives as
// `storage`, and `useSyncExternalStore` reads the value again on either.

import { useCallback, useSyncExternalStore } from "react";

export const KEYBOARD_MODE_KEY = "claims.keyboardMode";
const CHANGED = "claims-keyboard-mode-changed";

const read = (): boolean => {
  try {
    return window.localStorage.getItem(KEYBOARD_MODE_KEY) === "1";
  } catch {
    // Storage blocked (a private window): the mode is off.
    return false;
  }
};

const subscribe = (onChange: () => void): (() => void) => {
  window.addEventListener(CHANGED, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(CHANGED, onChange);
    window.removeEventListener("storage", onChange);
  };
};

/** Whether keyboard mode is on, and the way to turn it on or off. */
export const useKeyboardMode = (): [boolean, (on: boolean) => void] => {
  const on = useSyncExternalStore(subscribe, read, () => false);
  const set = useCallback((next: boolean) => {
    try {
      if (next) window.localStorage.setItem(KEYBOARD_MODE_KEY, "1");
      else window.localStorage.removeItem(KEYBOARD_MODE_KEY);
    } catch {
      // Storage blocked: the mode stays off, as `read` says.
    }
    window.dispatchEvent(new Event(CHANGED));
  }, []);
  return [on, set];
};
